import React from 'react';
import { Download, Send, Printer, Share2 } from 'lucide-react';
import Button from '../../../components/ui/Button';

const InvoicePreview = ({ invoiceData, onDownloadPDF, onSendEmail }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0)?.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  if (!invoiceData?.customer) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        <div className="text-center">
          <p className="text-sm">Select a customer to preview invoice</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Preview Actions */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          iconName="Download"
          onClick={onDownloadPDF}
          className="flex-1"
        />
        <Button
          variant="outline"
          size="sm"
          iconName="Printer"
          onClick={() => window.print()}
          className="flex-1"
        />
      </div>
      {/* Invoice Preview */}
      <div className="bg-white border rounded-lg overflow-hidden" style={{ fontSize: '11px' }}>
        {/* Header */}
        <div className="p-4 bg-primary text-primary-foreground">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-lg font-bold">INVOICE</h1>
              <p className="text-sm opacity-90">{invoiceData?.invoiceNumber}</p>
            </div>
            <div className="text-right text-sm">
              <p className="font-medium">Your Company Name</p>
              <p className="opacity-90">123 Business Street</p>
              <p className="opacity-90">City, State 12345</p>
            </div>
          </div>
        </div>

        {/* Invoice Details */}
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Bill To */}
            <div>
              <h3 className="font-semibold text-xs uppercase tracking-wide text-gray-600 mb-2">
                Bill To:
              </h3>
              <div className="space-y-1">
                <p className="font-medium">{invoiceData?.customer?.label}</p>
                <p className="text-gray-600 whitespace-pre-line">
                  {invoiceData?.customer?.address}
                </p>
                <p className="text-gray-600">{invoiceData?.customer?.email}</p>
                <p className="text-gray-600">{invoiceData?.customer?.phone}</p>
              </div>
            </div>

            {/* Invoice Info */}
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Invoice Date:</span>
                <span>{formatDate(invoiceData?.invoiceDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Due Date:</span>
                <span>{formatDate(invoiceData?.dueDate)}</span>
              </div>
              {invoiceData?.poNumber && (
                <div className="flex justify-between">
                  <span className="text-gray-600">PO Number:</span>
                  <span>{invoiceData?.poNumber}</span>
                </div>
              )}
            </div>
          </div>

          {/* Line Items */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Description
                  </th>
                  <th className="text-center py-2 text-xs font-semibold text-gray-600 uppercase tracking-wide w-16">
                    Qty
                  </th>
                  <th className="text-right py-2 text-xs font-semibold text-gray-600 uppercase tracking-wide w-20">
                    Rate
                  </th>
                  <th className="text-right py-2 text-xs font-semibold text-gray-600 uppercase tracking-wide w-20">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invoiceData?.lineItems?.map((item, index) => (
                  item?.description && (
                    <tr key={index}>
                      <td className="py-2">
                        <div>
                          <p className="font-medium">{item?.description}</p>
                          {item?.taxRate > 0 && (
                            <p className="text-gray-500 text-xs">Tax: {item?.taxRate}%</p>
                          )}
                        </div>
                      </td>
                      <td className="py-2 text-center">{item?.quantity}</td>
                      <td className="py-2 text-right">{formatCurrency(item?.rate)}</td>
                      <td className="py-2 text-right font-medium">{formatCurrency(item?.amount)}</td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="border-t pt-4">
            <div className="space-y-1 max-w-sm ml-auto">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>{formatCurrency(invoiceData?.subtotal)}</span>
              </div>
              
              {invoiceData?.discountAmount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount:</span>
                  <span>-{formatCurrency(invoiceData?.discountAmount)}</span>
                </div>
              )}
              
              <div className="flex justify-between">
                <span>Tax:</span>
                <span>{formatCurrency(invoiceData?.taxAmount)}</span>
              </div>
              
              <div className="flex justify-between font-bold text-base border-t pt-2">
                <span>Total:</span>
                <span>{formatCurrency(invoiceData?.total)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoiceData?.notes && (
            <div className="pt-4 border-t">
              <h3 className="font-semibold text-xs uppercase tracking-wide text-gray-600 mb-2">
                Notes:
              </h3>
              <p className="text-gray-700 whitespace-pre-wrap">{invoiceData?.notes}</p>
            </div>
          )}

          {/* Terms */}
          {invoiceData?.termsAndConditions && (
            <div className="pt-4 border-t">
              <h3 className="font-semibold text-xs uppercase tracking-wide text-gray-600 mb-2">
                Terms & Conditions:
              </h3>
              <p className="text-gray-700 text-xs whitespace-pre-wrap">
                {invoiceData?.termsAndConditions}
              </p>
            </div>
          )}
        </div>
      </div>
      {/* Send Options */}
      <div className="space-y-2">
        <Button
          variant="default"
          iconName="Send"
          iconPosition="left"
          onClick={onSendEmail}
          className="w-full"
        >
          Send Invoice
        </Button>
        
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            iconName="Share2"
            iconPosition="left"
            size="sm"
            onClick={() => {
              // Copy invoice link to clipboard
              navigator.clipboard?.writeText(window.location?.href);
              alert('Invoice link copied to clipboard!');
            }}
          >
            Share Link
          </Button>
          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
            size="sm"
            onClick={onDownloadPDF}
          >
            Download
          </Button>
        </div>
      </div>
      {/* Invoice Status */}
      <div className="text-center p-3 bg-gray-50 rounded-md">
        <p className="text-xs text-gray-600">
          This is a preview. The invoice will be generated when sent.
        </p>
      </div>
    </div>
  );
};

export default InvoicePreview;